import { mdiDelete, mdiPencil } from '../../components/icons';
import { useEffect, useState } from 'preact/hooks';
import { Api, type PasskeyInfo, type SessionInfo } from '../../api';
import { Svg } from '../../components/Icon';
import { formatDate, Spinner } from '../../components/ui';
import { registerPasskey } from '../../lib/passkey';
import { attempt } from '../../state';

function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'Unknown OS';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  return `${browser} on ${os}`;
}

export function Security() {
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);

  const reload = async () => {
    setPasskeys((await Api.passkeys()).passkeys);
    setSessions((await Api.sessions()).sessions);
  };
  useEffect(() => {
    void attempt(reload);
  }, []);

  async function addPasskey() {
    const ok = await attempt(
      () => registerPasskey('/api/me/passkeys/options', '/api/me/passkeys/verify', {}, 'name'),
      'Passkey added.'
    );
    if (ok) await reload();
  }

  async function rename(p: PasskeyInfo) {
    const name = prompt('Passkey name', p.name)?.trim();
    if (!name || name === p.name) return;
    await attempt(() => Api.renamePasskey(p.id, name));
    await reload();
  }

  async function remove(p: PasskeyInfo) {
    if (!confirm(`Remove "${p.name}"? You won't be able to sign in with it any more.`)) return;
    await attempt(() => Api.deletePasskey(p.id), 'Passkey removed.');
    await reload();
  }

  if (!passkeys || !sessions) return <Spinner />;
  return (
    <>
      <h2>Passkeys</h2>
      <p class="muted">
        Add a passkey on each device you use, or keep one on your phone and scan the QR code when
        signing in elsewhere. If you lose all of them, an admin can reset your account.
      </p>
      <ul class="rows">
        {passkeys.map((p) => (
          <li key={p.id} class="row">
            <div>
              <strong>{p.name}</strong>
              <span class="muted">
                {p.backedUp ? 'Synced' : 'This device only'} · added {formatDate(p.createdAt)} · last
                used {formatDate(p.lastUsedAt)}
              </span>
            </div>
            <span class="row-actions">
              <button type="button" class="icon-button" onClick={() => rename(p)} aria-label={`Rename ${p.name}`}>
                <Svg path={mdiPencil} size={18} />
              </button>
              <button
                type="button"
                class="icon-button"
                onClick={() => remove(p)}
                disabled={passkeys.length === 1}
                title={passkeys.length === 1 ? "You can't remove your only passkey" : undefined}
                aria-label={`Remove ${p.name}`}
              >
                <Svg path={mdiDelete} size={18} />
              </button>
            </span>
          </li>
        ))}
      </ul>
      <button type="button" class="button button-primary" onClick={addPasskey}>
        Add a passkey
      </button>

      <h2>Signed-in sessions</h2>
      <ul class="rows">
        {sessions.map((s) => (
          <li key={s.id} class="row">
            <div>
              <strong>
                {describeAgent(s.userAgent)} {s.current && <span class="badge">this device</span>}
              </strong>
              <span class="muted">
                Signed in {formatDate(s.createdAt)} · last active {formatDate(s.lastSeenAt)}
              </span>
            </div>
            {!s.current && (
              <button
                type="button"
                class="button"
                onClick={async () => {
                  await attempt(() => Api.revokeSession(s.id));
                  await reload();
                }}
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
      {sessions.length > 1 && (
        <button
          type="button"
          class="button"
          onClick={async () => {
            await attempt(() => Api.revokeOtherSessions(), 'Signed out everywhere else.');
            await reload();
          }}
        >
          Sign out all other sessions
        </button>
      )}
    </>
  );
}
